import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText } from "lucide-react";
import { useSeason } from "@/context/SeasonContext";
import { useSeasonCategories } from "@/hooks/useSeasonCategories";
import CategoryFilter from "@/components/CategoryFilter";
import CategoryBadges from "@/components/CategoryBadges";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";
import { useAuth } from "@/context/AuthContext";

export default function Reports() {
  const [athletes, setAthletes] = useState([]);
  const [sel, setSel] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { selectedSeasonId } = useSeason();
  const { user } = useAuth();
  const isAthlete = user?.role === "athlete";
  const { categories, catMap, filterByCategory } = useSeasonCategories(selectedSeasonId);

  useEffect(() => {
    api.get("/athletes?include_archived=1").then((r) => {
      setAthletes(r.data);
      if (isAthlete && r.data.length === 1) setSel(r.data[0].id);  // atleta: solo la propria scheda
    });
  }, [isAthlete]);

  const dl = (fmt) => {
    if (!sel) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/athlete/${sel}?fmt=${fmt}`;
    window.open(url, "_blank");
  };

  const filtered = filterByCategory(athletes, categoryFilter);
  // Reset selection if filtered out
  if (sel && !filtered.find((a) => a.id === sel)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setTimeout(() => setSel(""), 0);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Report</h1>
        <p className="text-zinc-500 mt-1">Esportazioni PDF ed Excel.</p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md p-6 max-w-2xl space-y-5">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-zinc-400" />
          <h2 className="font-heading font-semibold text-lg">Report atleta</h2>
        </div>
        {!isAthlete && <div className="flex items-center gap-3 flex-wrap">
          <CategoryFilter categories={categories} value={categoryFilter} onChange={setCategoryFilter} testId="reports-category-filter" className="w-56 h-12" />
          <span className="text-xs text-zinc-500">{filtered.length} atlet{filtered.length === 1 ? "a" : "i"} disponibili</span>
        </div>}
        {isAthlete ? (
          <div className="text-sm" data-testid="report-own-athlete">{athletes[0] ? displayName(athletes[0]) : "Scheda atleta non collegata: report non disponibile."}</div>
        ) : (
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid="report-athlete-select" className="h-12"><SelectValue placeholder="Seleziona atleta" /></SelectTrigger>
          <SelectContent className="max-h-96">
            {sortAthletes(filtered).map((a) => {
              const cats = (catMap[a.id] || []).map((c) => c.display_name).join(" · ");
              return (
                <SelectItem key={a.id} value={a.id}>
                  {displayName(a)}{cats ? ` — ${cats}` : ""}
                </SelectItem>
              );
            })}
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-zinc-500">Nessun atleta nella categoria selezionata</div>}
          </SelectContent>
        </Select>
        )}
        {sel && (
          <div className="text-xs text-zinc-600">
            <CategoryBadges categories={catMap[sel] || []} size="sm" />
          </div>
        )}
        <div className="flex gap-3">
          <Button data-testid="report-pdf" disabled={!sel} onClick={() => dl("pdf")} className="h-12 bg-black hover:bg-zinc-800"><Download className="w-4 h-4 mr-2" />Scarica PDF</Button>
          <Button data-testid="report-excel" disabled={!sel} onClick={() => dl("excel")} variant="outline" className="h-12"><Download className="w-4 h-4 mr-2" />Scarica Excel</Button>
        </div>
      </div>
    </div>
  );
}
